import React, { useState } from 'react';

import classNames from 'classnames';
import InfoIcon from 'icons/info.svg';
import ProgressIcon from 'icons/progress.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { useInfiniteProjectsAssignablePersonnelSearchesQuery } from 'components/hooks/projectsAssignablePersonnelSearchQueries';
import { useProjectsSignatureStatusQuery } from 'components/hooks/projectsSignatureStatusQueries';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import Checkbox from 'components/application/Checkbox';
import CollectionSelect from 'components/application/CollectionSelect';
import ModalBox from 'components/application/ModalBox';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

import { sortRolesByPrimaryAndPosition } from 'components/helpers/personnel';
import { personDisplayName } from 'components/helpers/users';

export default function ConfirmationModal({
  errorMessage,
  isSubmitting,
  onClose,
  onSubmitClick,
  personnelIds,
}) {
  const { projectId } = useProjectPersonnelContext();

  const [
    selectedCompanyRoleIdForPersonnel,
    setSelectedCompanyRoleIdForPersonnel,
  ] = useState(new Map());

  const [deselectedPersonnelIds, setDeselectedPersonnelIds] = useState(
    new Set(),
  );

  const selectedPersonnelIds = new Set(
    [...personnelIds].filter(
      (personnelId) => !deselectedPersonnelIds.has(personnelId),
    ),
  );

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectPersonnel.components.assignPersonnelSidePanel.components.ConfirmationModal',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const {
    data: domainAssignablePersonnel,
    fetchNextPage: fetchNextDomainAssignablePersonnelPage,
    hasNextPage: hasNextDomainAssignablePersonnelPage,
    isFetching: isDomainAssignablePersonnelFetching,
    isSuccess: isDomainAssignablePersonnelSuccess,
  } = useInfiniteProjectsAssignablePersonnelSearchesQuery({
    params: { filter: { ids: { in: [...personnelIds] } } },
    projectId,
    queryOptions: { enabled: personnelIds.size > 0 },
  });

  const { data: domainSignatureStatus } = useProjectsSignatureStatusQuery({
    projectId,
  });

  if (personnelIds.size === 0 && !errorMessage) return null;

  const handleCompanyRoleChange = ({ companyRoleId, personnelId }) => {
    setSelectedCompanyRoleIdForPersonnel((prevState) => {
      const newSelectedCompanyRoleIdForPersonnel = new Map(prevState);
      newSelectedCompanyRoleIdForPersonnel.set(personnelId, companyRoleId);
      return newSelectedCompanyRoleIdForPersonnel;
    });
  };

  const handlePersonnelToggle = (personnelId) => {
    setDeselectedPersonnelIds((prevState) => {
      const newDeselectedPersonnelIds = new Set(prevState);

      if (prevState.has(personnelId)) {
        newDeselectedPersonnelIds.delete(personnelId);
      } else {
        newDeselectedPersonnelIds.add(personnelId);
      }

      return newDeselectedPersonnelIds;
    });
  };

  return (
    <ModalBox
      customFooter={
        <div className='tw-ml-auto tw-flex tw-items-center tw-gap-3'>
          <TextButton
            color='blue'
            disabled={isSubmitting}
            onClick={onClose}
            size='md'
          >
            {i18nTCommon('shared.cancel')}
          </TextButton>
          {personnelIds.size > 0 ?
            <FilledButton
              color='mint'
              disabled={
                !isDomainAssignablePersonnelSuccess ||
                isSubmitting ||
                selectedPersonnelIds.size === 0
              }
              onClick={() => {
                onSubmitClick(
                  [...selectedPersonnelIds].map((personnelId) => ({
                    company_role_id:
                      selectedCompanyRoleIdForPersonnel.get(personnelId) ??
                      null,
                    personnel_id: personnelId,
                  })),
                );
              }}
            >
              {isSubmitting ?
                <>
                  <ProgressIcon className='tw-mr-1' height={18} width={18} />
                  {i18nTCommon('shared.pleaseWait...')}
                </>
              : selectedPersonnelIds.size > 0 ?
                i18nTCommon('personnel.assignCountPersonnel', {
                  count: selectedPersonnelIds.size,
                })
              : i18nTCommon('personnel.assignPersonnel')}
            </FilledButton>
          : <FilledButton color='mint' onClick={onClose}>
              {i18nTCommon('shared.gotIt')}
            </FilledButton>
          }
        </div>
      }
      isOpen={true}
      // To avoid the modal box outgrowing the viewport and leaving the overlay scrollable, we cap its max height and width and make the children a flex column.
      modalBoxClassName='tw-max-h-[calc(100vh-160px)] tw-max-w-[560px] [&>section:first-of-type]:tw-flex [&>section:first-of-type]:tw-flex-col'
      onClose={onClose}
    >
      <div className='tw-flex tw-min-h-0 tw-flex-auto tw-flex-col tw-gap-6 tw-p-6 tw-pb-0 tw-font-inter'>
        <div className='tw-flex tw-flex-col tw-gap-2'>
          <p className='tw-mb-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
            {i18nT('title')}
          </p>
          <p className='tw-mb-0 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-600'>
            {i18nT('description', { count: selectedPersonnelIds.size })}
          </p>
        </div>
        {errorMessage && (
          <div className='tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-bg-blue-025 tw-p-3'>
            <p className='tw-m-0 tw-font-inter tw-text-m tw-tracking-tight tw-text-blue-800'>
              {errorMessage}
            </p>
          </div>
        )}
        {personnelIds.size > 0 && (
          <>
            {domainSignatureStatus?.data.relationships.requestedVersion
              .data && (
              <div className='tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-bg-green-025 tw-p-3'>
                <InfoIcon
                  className='[&_path]:tw-fill-green-800'
                  height={12}
                  width={12}
                />
                <p className='tw-m-0 tw-font-inter tw-text-m tw-tracking-tight tw-text-green-800'>
                  {i18nT('requestedVersionInformation')}
                </p>
              </div>
            )}
            <div className='tw-relative tw-max-h-[440px] tw-min-h-0 tw-flex-auto tw-overflow-y-auto'>
              {isDomainAssignablePersonnelSuccess && (
                <>
                  {domainAssignablePersonnel.data.map((personnel, index) => {
                    const {
                      attributes: { firstName, lastName },
                      id: personnelId,
                      relationships: {
                        profilePhoto: { data: profilePhotoData },
                        userCompanyRoles: { data: userCompanyRolesData } = {
                          data: [],
                        },
                      },
                    } = personnel;

                    const profilePhoto =
                      profilePhotoData &&
                      domainAssignablePersonnel.included.profilePhoto[
                        profilePhotoData.id
                      ];

                    const name = personDisplayName({
                      attributes: { firstName, lastName },
                    });

                    const isSelected = selectedPersonnelIds.has(personnelId);

                    return (
                      <div
                        className={classNames(
                          'tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-solid tw-border-grey-100 tw-py-4',
                          index !== 0 && 'tw-border-t-1',
                        )}
                        key={personnelId}
                      >
                        <div className='tw-px-2'>
                          <Checkbox
                            isChecked={isSelected}
                            isDisabled={isSubmitting}
                            label={i18nTCommon(
                              isSelected ?
                                'shared.deselectName'
                              : 'shared.selectName',
                              { interpolation: { escapeValue: false }, name },
                            )}
                            name='select-personnel'
                            onChange={() => handlePersonnelToggle(personnelId)}
                          />
                        </div>
                        <ProfilePhotoDisplay
                          componentSize='sm'
                          photoUrl={profilePhoto?.links?.croppedUrl}
                          profileInitials={`${firstName[0]}${lastName[0]}`}
                        />
                        <div
                          className={classNames(
                            'tw-flex tw-w-auto tw-flex-col tw-gap-1',
                            userCompanyRolesData.length > 0 ?
                              'tw-justify-between'
                            : 'tw-justify-center',
                          )}
                        >
                          <p className='tw-mb-0 tw-truncate tw-text-m tw-font-medium tw-text-grey-900'>
                            {name}
                          </p>
                          {userCompanyRolesData.length > 0 &&
                            (() => {
                              const userCompanyRoles = userCompanyRolesData.map(
                                ({ id: userCompanyRoleId }) =>
                                  domainAssignablePersonnel.included
                                    .userCompanyRole[userCompanyRoleId],
                              );

                              const companyRoles = userCompanyRoles.map(
                                (userCompanyRole) =>
                                  domainAssignablePersonnel.included
                                    .companyRole[
                                    userCompanyRole.relationships.companyRole
                                      .data.id
                                  ],
                              );

                              if (companyRoles.length === 1) {
                                return (
                                  <p className='tw-mb-0 tw-truncate tw-text-s tw-font-normal tw-text-grey-500'>
                                    {companyRoles[0].attributes.position}
                                  </p>
                                );
                              }

                              const options = sortRolesByPrimaryAndPosition(
                                userCompanyRoles,
                                companyRoles,
                              ).map((companyRole) => ({
                                label: companyRole.attributes.position,
                                value: companyRole.id,
                              }));

                              return (
                                <div className='tw-w-70'>
                                  <CollectionSelect
                                    externalProps={{
                                      closeMenuOnScroll: () => true,
                                      menuPlacement: 'auto',
                                      menuPosition: 'fixed',
                                    }}
                                    isRequired={true}
                                    modifiers={['inline-form']}
                                    name='companyRoleId'
                                    onChange={(option) => {
                                      handleCompanyRoleChange({
                                        companyRoleId: option.value,
                                        personnelId,
                                      });
                                    }}
                                    options={options}
                                    value={
                                      options.find(
                                        (option) =>
                                          option.value ===
                                          selectedCompanyRoleIdForPersonnel.get(
                                            personnelId,
                                          ),
                                      ) || options[0]
                                    }
                                  />
                                </div>
                              );
                            })()}
                        </div>
                      </div>
                    );
                  })}
                  {hasNextDomainAssignablePersonnelPage &&
                    !isDomainAssignablePersonnelFetching && (
                      <InView
                        as='div'
                        className='tw-h-1 tw-w-full'
                        onChange={(isInView) => {
                          if (isInView)
                            fetchNextDomainAssignablePersonnelPage();
                        }}
                      />
                    )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </ModalBox>
  );
}

ConfirmationModal.propTypes = {
  errorMessage: PropTypes.string,
  isSubmitting: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitClick: PropTypes.func.isRequired,
  personnelIds: PropTypes.instanceOf(Set).isRequired,
};
